import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Button,
    Typography,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Alert,
    FormHelperText,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import servicesList from "../Data/ServicesData";
import ServiceDropdown from "./Servicesdropdown";
import { apiUrl } from "./LoginSignup";
import Mailer from "./mail";
import BookingDocumentsModal from "./BookingDocumentsModal";

const FIELD_LABELS = {
    branch: "Branch",
    companyName: "Company Name",
    contactPerson: "Contact Person",
    contactNumber: "Contact Number",
    email: "Email",
    address: "Address",
    date: "Booking Date",
    services: "Services",
    totalAmount: "Total Amount",
    selectTerm: "Select Term",
    amount: "Received Amount",
    paymentDate: "Payment Date",
    pan: "PAN Number",
    state: "State",
};

const parseNumericAmount = (value, fieldLabel) => {
    const raw = String(value ?? "").trim();

    if (!raw) {
        return { valid: false, error: `${fieldLabel} is required` };
    }

    if (raw.includes(",")) {
        return {
            valid: false,
            error: `${fieldLabel}: Do not use commas. Enter numbers only (e.g. 177000 instead of 1,77,000)`,
        };
    }

    if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
        return {
            valid: false,
            error: `${fieldLabel}: Enter a valid number without symbols, spaces, or commas`,
        };
    }

    const num = Number(raw);
    if (Number.isNaN(num) || num <= 0) {
        return {
            valid: false,
            error: `${fieldLabel}: Must be a number greater than 0`,
        };
    }

    return { valid: true, value: num };
};

const AddBooking = ({ onClose }) => {
    const [showDocsPrompt, setShowDocsPrompt] = useState(false);
    const [showDocsModal, setShowDocsModal] = useState(false);
    const [createdBooking, setCreatedBooking] = useState(null);
    const [formData, setFormData] = useState({
        branch: "",
        companyName: "",
        contactPerson: "",
        contactNumber: "",
        email: "",
        date: new Date().toISOString().split("T")[0],
        services: [],
        totalAmount: "",
        selectTerm: "",
        amount: "",
        paymentDate: "",
        closed: "",
        pan: "",
        gst: "",
        notes: "",
        bank: "",
        state: "",
        funddisbursement: "",
        address: "",
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [validationMessages, setValidationMessages] = useState([]);
    const [banks, setBanks] = useState([]);

    useEffect(() => {
        const fetchBanks = async () => {
            try {
                const userSession = JSON.parse(localStorage.getItem("userSession"));
                const response = await fetch(`${apiUrl}/banks/api/banks`, {
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `${userSession?.token}`,
                    },
                });
                const data = await response.json();
                if (response.ok) {
                    setBanks(Array.isArray(data) ? data.filter((bank) => bank.status) : []);
                }
            } catch (error) {
                console.error("Error fetching banks", error);
            }
        };
        fetchBanks();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const showValidationPopup = (messages) => {
        const list = Array.isArray(messages) ? messages : [messages];
        setValidationMessages(list.filter(Boolean));
        setValidationDialogOpen(true);
    };

    const validate = () => {
        const validationErrors = {};

        if (!formData.branch) {
            validationErrors.branch = "Branch is required";
        }

        if (!formData.contactPerson?.trim()) {
            validationErrors.contactPerson = "Contact Person is required";
        }

        const contactNumberRegex = /^\d{10}$/;
        if (!formData.contactNumber) {
            validationErrors.contactNumber = "Contact Number is required";
        } else if (!contactNumberRegex.test(formData.contactNumber.replace(/\s/g, ""))) {
            validationErrors.contactNumber =
                "Contact Number must be exactly 10 digits (no spaces or symbols)";
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email?.trim()) {
            validationErrors.email = "Email is required";
        } else if (!emailRegex.test(formData.email.trim())) {
            validationErrors.email = "Enter a valid email address";
        }

        if (!formData.address?.trim()) {
            validationErrors.address = "Address is required";
        }

        if (!formData.date) {
            validationErrors.date = "Booking Date is required";
        }

        if (!formData.services?.length) {
            validationErrors.services = "At least one service must be selected";
        }

        const totalResult = parseNumericAmount(
            formData.totalAmount,
            FIELD_LABELS.totalAmount
        );
        if (!totalResult.valid) {
            validationErrors.totalAmount = totalResult.error.replace(
                `${FIELD_LABELS.totalAmount}: `,
                ""
            );
        }

        if (!formData.selectTerm) {
            validationErrors.selectTerm = "Select Term is required";
        }

        const amountResult = parseNumericAmount(
            formData.amount,
            FIELD_LABELS.amount
        );
        if (!amountResult.valid) {
            validationErrors.amount = amountResult.error.replace(
                `${FIELD_LABELS.amount}: `,
                ""
            );
        } else if (totalResult.valid && amountResult.value > totalResult.value) {
            validationErrors.amount =
                "Received Amount cannot be greater than Total Amount";
        }

        if (!formData.paymentDate) {
            validationErrors.paymentDate = "Payment Date is required";
        }

        if (!formData.pan?.trim()) {
            validationErrors.pan = "PAN Number is required";
        } else {
            const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
            if (!panRegex.test(formData.pan.trim().toUpperCase())) {
                validationErrors.pan =
                    "PAN must be 10 characters (e.g. ABCDE1234F) — uppercase letters and numbers only";
            }
        }

        if (!formData.state) {
            validationErrors.state = "State is required";
        }

        setErrors(validationErrors);

        const popupMessages = Object.entries(validationErrors).map(
            ([field, message]) => `${FIELD_LABELS[field] || field}: ${message}`
        );

        return {
            isValid: popupMessages.length === 0,
            popupMessages,
            parsedAmounts: {
                total: totalResult.valid ? totalResult.value : null,
                received: amountResult.valid ? amountResult.value : null,
            },
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { isValid, popupMessages, parsedAmounts } = validate();

        if (!isValid) {
            showValidationPopup(popupMessages);
            enqueueSnackbar("Please fix the errors before submitting.", {
                variant: "warning",
            });
            return;
        }

        setLoading(true);
        const userSession = JSON.parse(localStorage.getItem("userSession"));

        if (!userSession) {
            setLoading(false);
            showValidationPopup(["User session not found. Please log in again."]);
            return;
        }

        const dataToSubmit = {
            user_id: userSession.user_id,
            bdm: userSession.name,
            branch_name: formData.branch,
            company_name: formData.companyName.toUpperCase(),
            contact_person: formData.contactPerson.trim(),
            email: formData.email.trim(),
            contact_no: Number(formData.contactNumber.replace(/\s/g, "")),
            services: formData.services,
            total_amount: parsedAmounts.total,
            closed_by: formData.closed ? formData.closed : "",
            term_1: formData.selectTerm === "Term 1" ? parsedAmounts.received : null,
            term_2: formData.selectTerm === "Term 2" ? parsedAmounts.received : null,
            term_3: formData.selectTerm === "Term 3" ? parsedAmounts.received : null,
            payment_date: formData.paymentDate,
            pan: formData.pan.trim().toUpperCase(),
            gst: formData.gst ? formData.gst : "N/A",
            remark: formData.notes,
            date: formData.date,
            bank: formData.bank,
            state: formData.state,
            address: formData.address.trim(),
            status: "Pending",
            funddisbursement: formData.funddisbursement,
        };

        try {
            const response = await fetch(`${apiUrl}/booking/addbooking`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    authorization: `${userSession.token}`,
                },
                body: JSON.stringify(dataToSubmit),
            });

            const res = await response.json().catch(() => ({}));

            if (!response.ok) {
                const serverMessage =
                    res.message ||
                    res.Message ||
                    "Booking could not be submitted. Please check all fields and try again.";
                showValidationPopup([serverMessage]);
                enqueueSnackbar("Booking submission failed.", { variant: "error" });
                return;
            }

            if (res.booking?.term_1 != null) {
                try {
                    await Mailer({
                        email: res.booking.email,
                        name: res.booking.company_name,
                    });
                    enqueueSnackbar("Welcome mail sent successfully!", { variant: "success" });
                } catch (mailError) {
                    enqueueSnackbar(
                        mailError.message || "Booking saved, but welcome mail could not be sent.",
                        { variant: "warning" }
                    );
                }
            }

            const invoiceNo =
                res.invoice_number ?? res.booking?.invoice_number ?? null;
            enqueueSnackbar(
                `Booking created${invoiceNo ? ` (Invoice ${invoiceNo})` : ""}! Pending: ₹${
                    res.pending_amount ?? res.booking?.pending_amount ?? 0
                }`,
                { variant: "success" }
            );

            setFormData({
                branch: "",
                companyName: "",
                contactPerson: "",
                contactNumber: "",
                email: "",
                date: new Date().toISOString().split("T")[0],
                services: [],
                totalAmount: "",
                closed: "",
                selectTerm: "",
                amount: "",
                paymentDate: "",
                pan: "",
                gst: "",
                notes: "",
                bank: "",
                state: "",
                funddisbursement: "",
                address: "",
            });
            setErrors({});
            if (res.booking) {
                setCreatedBooking(res.booking);
                setShowDocsPrompt(true);
            } else if (onClose) {
                onClose();
            }

        } catch (error) {
            console.error("Error:", error);
            showValidationPopup([
                error.message ||
                    "Network error. Could not submit booking. Please try again.",
            ]);
            enqueueSnackbar("Error creating booking.", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            paddingTop: 4,
            paddingBottom: 4,
            paddingLeft: { xs: 2, sm: 10, md: 20 },
            paddingRight: { xs: 2, sm: 10, md: 20 },
            backgroundColor: "#f9f9f9",
            borderRadius: 2,
            boxShadow: 3,
        }}>
            <Typography variant="h5" component="h2" sx={{
                marginBottom: 3,
                textAlign: "center",
                fontWeight: "bold",
                color: "#333",
            }}>
                Create New Booking
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={Boolean(errors.branch)}>
                            <InputLabel>Branch *</InputLabel>
                            <Select
                                name="branch"
                                value={formData.branch}
                                onChange={handleChange}
                                label="Branch *"
                                variant="outlined"
                            >
                                <MenuItem value="">Select branch</MenuItem>
                                <MenuItem value="308">308</MenuItem>
                            </Select>
                            {errors.branch && (
                                <FormHelperText>{errors.branch}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Company Name"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            placeholder="Enter company name"
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Contact Person Name *"
                            name="contactPerson"
                            value={formData.contactPerson}
                            onChange={handleChange}
                            placeholder="Enter contact person name"
                            variant="outlined"
                            error={Boolean(errors.contactPerson)}
                            helperText={errors.contactPerson}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Contact Number *"
                            name="contactNumber"
                            type="text"
                            value={formData.contactNumber}
                            onChange={handleChange}
                            placeholder="10 digit mobile number"
                            variant="outlined"
                            error={Boolean(errors.contactNumber)}
                            helperText={errors.contactNumber}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email ID *"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter email ID"
                            variant="outlined"
                            error={Boolean(errors.email)}
                            helperText={errors.email}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Address *"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            placeholder="Enter company address"
                            variant="outlined"
                            error={Boolean(errors.address)}
                            helperText={errors.address}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Booking Date *"
                            name="date"
                            type="date"
                            value={formData.date}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>

                    <ServiceDropdown
                        formData={formData}
                        setFormData={setFormData}
                        error={errors.services}
                    />

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Total Amount *"
                            name="totalAmount"
                            type="text"
                            value={formData.totalAmount}
                            onChange={handleChange}
                            placeholder="Numbers only, no commas (e.g. 177000)"
                            variant="outlined"
                            error={Boolean(errors.totalAmount)}
                            helperText={
                                errors.totalAmount ||
                                "Enter amount without commas or symbols"
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={Boolean(errors.selectTerm)}>
                            <InputLabel>Select Term *</InputLabel>
                            <Select
                                name="selectTerm"
                                value={formData.selectTerm}
                                onChange={handleChange}
                                label="Select Term *"
                                variant="outlined"
                            >
                                <MenuItem value="">Select Term</MenuItem>
                                <MenuItem value="Term 1">Term 1</MenuItem>
                                <MenuItem value="Term 2">Term 2</MenuItem>
                                <MenuItem value="Term 3">Term 3</MenuItem>
                                <MenuItem value="Term 4">Term 4</MenuItem>
                                <MenuItem value="Term 5">Term 5</MenuItem>
                            </Select>
                            {errors.selectTerm && (
                                <FormHelperText>{errors.selectTerm}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    {["Term 2", "Term 3", "Term 4", "Term 5"].includes(formData.selectTerm) && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                {formData.selectTerm} creates a <strong>new booking with a new invoice number</strong>,
                                linked to the existing deal (same <strong>Company Name</strong> + <strong>PAN</strong>).
                                Pending becomes: previous pending − this received amount.
                            </Alert>
                        </Grid>
                    )}


                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Received Amount *"
                            name="amount"
                            type="text"
                            value={formData.amount}
                            onChange={handleChange}
                            placeholder="Numbers only, no commas (e.g. 88500)"
                            variant="outlined"
                            error={Boolean(errors.amount)}
                            helperText={
                                errors.amount ||
                                "Enter amount without commas or symbols"
                            }
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Closed By"
                            name="closed"
                            value={formData.closed}
                            onChange={handleChange}
                            placeholder="Lead closed by"
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Payment Date *"
                            name="paymentDate"
                            type="date"
                            value={formData.paymentDate}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            error={Boolean(errors.paymentDate)}
                            helperText={errors.paymentDate}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="PAN Number *"
                            name="pan"
                            value={formData.pan}
                            onChange={handleChange}
                            placeholder="e.g. ABCDE1234F"
                            variant="outlined"
                            error={Boolean(errors.pan)}
                            helperText={errors.pan}
                            inputProps={{ style: { textTransform: "uppercase" } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="GST Number"
                            name="gst"
                            value={formData.gst}
                            onChange={handleChange}
                            placeholder="Enter GST"
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Payment Mode</InputLabel>
                            <Select
                                name="bank"
                                value={formData.bank}
                                onChange={handleChange}
                                label="Payment Mode"
                                variant="outlined"
                            >
                                <MenuItem value="">Select Payment Mode</MenuItem>
                                {banks.map((bank) => (
                                    <MenuItem key={bank._id} value={bank.name}>
                                        {bank.name}
                                    </MenuItem>
                                ))}
                                <MenuItem value="Razorpay">Razorpay</MenuItem>
                                <MenuItem value="Cash">Cash</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="After Fund Disbursement"
                            name="funddisbursement"
                            value={formData.funddisbursement}
                            onChange={handleChange}
                            placeholder="Enter percentage"
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={Boolean(errors.state)}>
                            <InputLabel>Select State *</InputLabel>
                            <Select
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                label="Select State *"
                                variant="outlined"
                            >
                                <MenuItem value="">Select State</MenuItem>
                                <MenuItem value="Andhra Pradesh">Andhra Pradesh</MenuItem>
                                <MenuItem value="Arunachal Pradesh">Arunachal Pradesh</MenuItem>
                                <MenuItem value="Assam">Assam</MenuItem>
                                <MenuItem value="Bihar">Bihar</MenuItem>
                                <MenuItem value="Chhattisgarh">Chhattisgarh</MenuItem>
                                <MenuItem value="Goa">Goa</MenuItem>
                                <MenuItem value="Gujarat">Gujarat</MenuItem>
                                <MenuItem value="Haryana">Haryana</MenuItem>
                                <MenuItem value="Himachal Pradesh">Himachal Pradesh</MenuItem>
                                <MenuItem value="Jharkhand">Jharkhand</MenuItem>
                                <MenuItem value="Karnataka">Karnataka</MenuItem>
                                <MenuItem value="Kerala">Kerala</MenuItem>
                                <MenuItem value="Madhya Pradesh">Madhya Pradesh</MenuItem>
                                <MenuItem value="Maharashtra">Maharashtra</MenuItem>
                                <MenuItem value="Manipur">Manipur</MenuItem>
                                <MenuItem value="Meghalaya">Meghalaya</MenuItem>
                                <MenuItem value="Mizoram">Mizoram</MenuItem>
                                <MenuItem value="Nagaland">Nagaland</MenuItem>
                                <MenuItem value="Odisha">Odisha</MenuItem>
                                <MenuItem value="Punjab">Punjab</MenuItem>
                                <MenuItem value="Chandigarh">Chandigarh</MenuItem>
                                <MenuItem value="Rajasthan">Rajasthan</MenuItem>
                                <MenuItem value="Sikkim">Sikkim</MenuItem>
                                <MenuItem value="Tamil Nadu">Tamil Nadu</MenuItem>
                                <MenuItem value="Telangana">Telangana</MenuItem>
                                <MenuItem value="Tripura">Tripura</MenuItem>
                                <MenuItem value="Uttar Pradesh">Uttar Pradesh</MenuItem>
                                <MenuItem value="Uttarakhand">Uttarakhand</MenuItem>
                                <MenuItem value="West Bengal">West Bengal</MenuItem>
                                <MenuItem value="Delhi">Delhi</MenuItem>
                                <MenuItem value="Andaman and Nicobar">Andaman and Nicobar</MenuItem>
                                <MenuItem value="Jammu and Kashmir">Jammu and Kashmir</MenuItem>
                            </Select>
                            {errors.state && (
                                <FormHelperText>{errors.state}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Notes"
                            name="notes"
                            value={formData.notes}
                            onChange={handleChange}
                            placeholder="Enter any notes"
                            multiline
                            rows={3}
                            variant="outlined"
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            fullWidth
                            disabled={loading}
                            sx={{
                                backgroundColor: "#1976d2",
                                "&:hover": { backgroundColor: "#115293" },
                            }}
                        >
                            {loading ? "Submitting..." : "Submit Booking"}
                        </Button>
                    </Grid>
                </Grid>
            </form>

            {loading && (
                <Box sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    marginTop: "20px",
                }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            <Dialog
                open={validationDialogOpen}
                onClose={() => setValidationDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: "#b91c1c" }}>
                    Please correct the following
                </DialogTitle>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Booking was not submitted. Fix the issues below and try again.
                    </Alert>
                    <List dense>
                        {validationMessages.map((msg, index) => (
                            <ListItem key={index} sx={{ alignItems: "flex-start" }}>
                                <ListItemText
                                    primary={`${index + 1}. ${msg}`}
                                    primaryTypographyProps={{ fontSize: "0.95rem" }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setValidationDialogOpen(false)}
                        variant="contained"
                        autoFocus
                    >
                        OK, I&apos;ll Fix These
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Prompt Dialog to Attach Documents right after Booking Creation */}
            <Dialog
                open={showDocsPrompt}
                onClose={() => {
                    setShowDocsPrompt(false);
                    if (onClose) onClose();
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: "#0B1E7A", fontWeight: 700 }}>
                    Booking Created Successfully!
                </DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Booking for <strong>{createdBooking?.company_name}</strong> has been registered.
                    </Alert>
                    <Typography variant="body1" sx={{ color: "#475569" }}>
                        Would you like to attach client documents (PAN, GST, Registration, KYC files) to this booking now?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button
                        onClick={() => {
                            setShowDocsPrompt(false);
                            if (onClose) onClose();
                        }}
                        variant="outlined"
                        color="secondary"
                    >
                        Skip for Now
                    </Button>
                    <Button
                        onClick={() => {
                            setShowDocsPrompt(false);
                            setShowDocsModal(true);
                        }}
                        variant="contained"
                        sx={{ backgroundColor: "#0B1E7A", "&:hover": { backgroundColor: "#091862" } }}
                        autoFocus
                    >
                        Attach Documents Now
                    </Button>
                </DialogActions>
            </Dialog>

            {showDocsModal && createdBooking && (
                <BookingDocumentsModal
                    open={showDocsModal}
                    booking={createdBooking}
                    bookingId={createdBooking._id}
                    onClose={() => {
                        setShowDocsModal(false);
                        if (onClose) onClose();
                    }}
                />
            )}
        </Box>
    );
};

export default AddBooking;

